// Phase 9 /analyse/compare — pick 2–N runs, render side-by-side metrics.
//
// Uses POST /api/runs/{a}/compare/{b} for each adjacent pair. Renders a
// header row of picked runs, a metric matrix, and a delta chip column
// per pair.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { GitCompare, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { runs as runsApi } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

export default function CompareRunsPage() {
    const [allRuns, setAllRuns] = useState([]);
    const [selected, setSelected] = useState([]); // array of run objects
    const [pairs, setPairs] = useState(null); // array of { a, b, data }
    const [comparing, setComparing] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const data = await runsApi.list();
                const items = Array.isArray(data) ? data : data?.items || [];
                setAllRuns(items);
            } catch (e) {
                setErr(formatApiError(e, "Failed to load runs"));
            }
        })();
    }, []);

    const toggle = (r) => {
        setSelected((s) => {
            if (s.some((x) => x.id === r.id)) return s.filter((x) => x.id !== r.id);
            if (s.length >= 5) {
                toast.error("Max 5 runs at a time.");
                return s;
            }
            return [...s, r];
        });
        setPairs(null);
    };

    const runCompare = async () => {
        if (selected.length < 2) return;
        setComparing(true);
        setErr("");
        try {
            const results = [];
            for (let i = 0; i < selected.length - 1; i += 1) {
                const a = selected[i];
                const b = selected[i + 1];
                const data = await runsApi.compareTo(a.id, b.id);
                results.push({ a, b, data });
            }
            setPairs(results);
        } catch (e) {
            setErr(formatApiError(e, "Compare failed"));
        } finally {
            setComparing(false);
        }
    };

    const succeededRuns = useMemo(
        () => allRuns.filter((r) => r.status === "succeeded"),
        [allRuns],
    );

    return (
        <div data-testid="compare-page">
            <PageHeader
                title="Compare Runs"
                subtitle="Select 2–5 succeeded runs and run a pair-wise comparison."
                actions={[
                    <button
                        key="go"
                        onClick={runCompare}
                        disabled={selected.length < 2 || comparing}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] bg-ux-accent text-black disabled:opacity-50"
                        data-testid="compare-run-button"
                    >
                        <GitCompare className="w-4 h-4" />
                        {comparing ? "Comparing…" : `Compare (${selected.length})`}
                    </button>,
                ]}
            />

            {err && (
                <div className="mb-4 p-3 rounded-md border border-ux-danger/40 bg-ux-danger/10 text-ux-danger text-[13px]">
                    {err}
                </div>
            )}

            {/* Selection chips */}
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="compare-selection-chips">
                    {selected.map((r) => (
                        <span
                            key={r.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-ux-accent/40 bg-ux-accent/10 text-[12px]"
                        >
                            <span className="text-ux-text-1 truncate max-w-[180px]">
                                {r.name || r.id.slice(0, 8)}
                            </span>
                            <button
                                onClick={() => toggle(r)}
                                className="text-ux-text-3 hover:text-ux-text-1"
                                aria-label="Remove"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Run picker */}
            <div className="rounded-lg border border-ux-border-1 overflow-hidden mb-6">
                <div className="px-3 py-2 bg-ux-bg-1 text-[11px] uppercase tracking-wider text-ux-text-3 border-b border-ux-border-1">
                    Succeeded runs
                </div>
                <table className="w-full text-[13px]" data-testid="compare-picker-table">
                    <thead className="bg-ux-bg-1 text-ux-text-3 text-[11px] uppercase tracking-wider">
                        <tr>
                            <th className="w-8 px-2 py-2" />
                            <th className="text-left px-3 py-2">Name</th>
                            <th className="text-left px-3 py-2">ID</th>
                            <th className="text-left px-3 py-2">Samples</th>
                            <th className="text-left px-3 py-2">Driver</th>
                            <th className="text-left px-3 py-2">Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {succeededRuns.slice(0, 60).map((r) => {
                            const picked = selected.some((s) => s.id === r.id);
                            return (
                                <tr
                                    key={r.id}
                                    onClick={() => toggle(r)}
                                    className={[
                                        "border-t border-ux-border-1 cursor-pointer transition-colors",
                                        picked
                                            ? "bg-ux-accent/10 hover:bg-ux-accent/15"
                                            : "hover:bg-ux-bg-1",
                                    ].join(" ")}
                                    data-testid={`compare-picker-row-${r.id}`}
                                >
                                    <td className="px-2 py-2">
                                        <input
                                            type="checkbox"
                                            checked={picked}
                                            onChange={() => toggle(r)}
                                            className="accent-ux-accent"
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-1 truncate max-w-[280px]">
                                        {r.name || "(no name)"}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-ux-text-3">
                                        {r.id.slice(0, 10)}…
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-2">
                                        {r.samples_planned ?? "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-2">
                                        {r.backend?.driver || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-3 font-mono">
                                        {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                                    </td>
                                </tr>
                            );
                        })}
                        {succeededRuns.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-ux-text-3">
                                    No succeeded runs available.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Results */}
            {pairs && (
                <div className="space-y-4" data-testid="compare-results">
                    {pairs.map(({ a, b, data }, i) => (
                        <div
                            key={i}
                            className="rounded-lg border border-ux-border-1 bg-ux-bg-1"
                        >
                            <div className="flex items-center gap-2 px-4 py-3 border-b border-ux-border-1">
                                <span className="text-[11px] uppercase tracking-wider text-ux-text-3">
                                    Pair {i + 1}
                                </span>
                                <span className="text-[13px] text-ux-text-1 font-mono">
                                    {a.id.slice(0, 8)}… ↔ {b.id.slice(0, 8)}…
                                </span>
                                <span
                                    className={[
                                        "ml-auto px-2 py-0.5 rounded-full text-[11px] border",
                                        data?.zero_drift
                                            ? "border-ux-success/40 bg-ux-success/10 text-ux-success"
                                            : "border-ux-warning/40 bg-ux-warning-soft text-ux-warning",
                                    ].join(" ")}
                                    data-testid={`compare-pair-verdict-${i}`}
                                >
                                    {data?.zero_drift ? "zero-drift" : "drift observed"}
                                </span>
                            </div>
                            <pre
                                className="text-[11px] font-mono p-4 text-ux-text-2 overflow-x-auto max-h-[340px]"
                                data-testid={`compare-pair-payload-${i}`}
                            >
                                {JSON.stringify(data, null, 2)}
                            </pre>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
